import { toInputDate } from "@/utils/dateUtils";
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Plus,
  X,
  Upload,
  Image as ImageIcon,
  Info,
  MessageSquare,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

import { useVehicleCard } from "@/hooks/useVehicleCard";
import { formatMileage } from "@/utils/listingUtils";
import type {
  VehicleCardImages,
  VehicleCardExteriorImage,
  VehicleCardDamageImage,
} from "@/types/vehicleCard";
import type { FileObject } from "@/types/shared/file";
import { ModalInfoVehicleImages } from "@/components/car-post-ad/components/ModalInfoVehicleImages";
import { useLanguage } from "@/context/LanguageContext";
import { vehicleCardUILabels } from "@/i18n";

interface VehicleCardImagesProps {
  images?: VehicleCardImages;
  vehicleCardId?: string;
}

const VehicleCardImagesComponent = ({
  images = {} as VehicleCardImages,
  vehicleCardId,
}: VehicleCardImagesProps) => {
  const { language } = useLanguage();
  const [currentImages, setCurrentImages] = useState<VehicleCardImages>(images);
  const [isLoading, setIsLoading] = useState(true);
  // Track uploading/deleting images by ID for individual loading indicators
  const [uploadingImageIds, setUploadingImageIds] = useState<Set<string>>(
    new Set(),
  );
  const [deletingImageIds, setDeletingImageIds] = useState<Set<string>>(
    new Set(),
  );
  // Track loading state for exterior section (thumbnail vs regular)
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingRegularExterior, setUploadingRegularExterior] =
    useState(false);
  // Track tire image types (primary/secondary) by image ID since backend doesn't support this yet
  const [tireImageTypes, setTireImageTypes] = useState<
    Map<string, "primary" | "secondary">
  >(new Map());

  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    alt: string;
  } | null>(null);
  const [showServiceDetailDialog, setShowServiceDetailDialog] = useState(false);
  const [newServiceDetail, setNewServiceDetail] = useState({
    date: toInputDate(new Date()),
    mileage: "",
    serviceType: "",
    notes: "",
  });
  const [expandedComments, setExpandedComments] = useState<{
    [key: number]: boolean;
  }>({});
  const [commentTexts, setCommentTexts] = useState<{
    [key: number]: string;
  }>({});

  const {
    addVehicleCardImage,
    removeVehicleCardImage,
    addVehicleCardServiceDetail,
    removeVehicleCardServiceDetail,
    updateVehicleCardDamageImageComment,
    isAddingVehicleCardImage,
    isRemovingVehicleCardImage,
    isAddingVehicleCardServiceDetail,
    isRemovingVehicleCardServiceDetail,
    isUpdatingVehicleCardDamageImageComment,
  } = useVehicleCard();

  // Helper function to sort exterior images (thumbnail first, then others)
  const sortExteriorImages = (
    images: VehicleCardExteriorImage[],
  ): VehicleCardExteriorImage[] => {
    return [...images].sort((a, b) => {
      const aIsThumbnail = a.isThumbnail || false;
      const bIsThumbnail = b.isThumbnail || false;

      // Thumbnail always comes first
      if (aIsThumbnail && !bIsThumbnail) return -1;
      if (!aIsThumbnail && bIsThumbnail) return 1;

      // Both are same type, maintain original order
      return 0;
    });
  };

  // Use ref to track if we've done initial load to prevent re-renders on refetches
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // When component mounts or vehicleCardId changes, show loading spinner only on initial load
    const isInitialLoad =
      isLoading && vehicleCardId && !hasInitializedRef.current;

    // Only update if images prop has actual data and we haven't initialized yet, or vehicleCardId changed
    const hasImages = images && Object.keys(images).length > 0;

    if (hasImages) {
      // Sort exterior images (thumbnail first)
      const sortedImages: VehicleCardImages = {
        ...images,
        exterior: images.exterior
          ? sortExteriorImages(images.exterior)
          : undefined,
        interior: images.interior,
        damage: images.damage,
        serviceImages: images.serviceImages,
        tires: images.tires,
      };

      // Always sync serviceDetails from props to ensure they're up to date
      // Update on initial load or when vehicleCardId changes (new card selected)
      // Also update serviceDetails on every refetch to sync with server
      if (isInitialLoad || !hasInitializedRef.current) {
        setCurrentImages(sortedImages);
        hasInitializedRef.current = true;
      } else {
        // On refetch, update serviceDetails and other props that might have changed
        setCurrentImages((prev) => ({
          ...prev,
          serviceDetails: sortedImages.serviceDetails || prev.serviceDetails,
        }));
      }

      // Hide loading after a delay only on initial load
      if (isInitialLoad) {
        const timer = setTimeout(() => {
          setIsLoading(false);
        }, 400);
        return () => clearTimeout(timer);
      }
    } else {
      // No images, hide loading immediately
      setIsLoading(false);
      hasInitializedRef.current = true;
    }
  }, [images, vehicleCardId, isLoading]); // Keep images in deps but use ref to prevent updates on refetches

  // Reset initialization flag when vehicleCardId changes (new card selected)
  useEffect(() => {
    hasInitializedRef.current = false;
    setIsLoading(true);
    // Reset serviceDetails when switching cards
    if (images?.serviceDetails) {
      setCurrentImages((prev) => ({
        ...prev,
        serviceDetails: images.serviceDetails,
      }));
    }
  }, [vehicleCardId, images?.serviceDetails]);

  // Process a single image file (eager upload)
  const processImageFile = async (
    file: File,
    section: keyof VehicleCardImages,
    isThumbnail: boolean = false,
    tireType?: "primary" | "secondary",
  ): Promise<string> => {
    if (!vehicleCardId) {
      throw new Error(vehicleCardUILabels(language).vehicleCardIdMissing);
    }

    // Map section to category
    let category: "exterior" | "interior" | "damage" | "service" | "tires";
    if (section === "exterior") {
      category = "exterior";
    } else if (section === "interior") {
      category = "interior";
    } else if (section === "damage") {
      category = "damage";
    } else if (section === "serviceImages") {
      category = "service";
    } else if (section === "tires") {
      category = "tires";
    } else {
      throw new Error(vehicleCardUILabels(language).invalidSection);
    }

    // Call the hook to add image (eager upload - uploads immediately)
    const uploadedImage = await addVehicleCardImage({
      vehicleCardId,
      category,
      file,
      isThumbnail,
      tireType,
    });

    // Add to uploading set with actual image ID for loading indicator
    setUploadingImageIds((prev) => new Set(prev).add(uploadedImage.id));

    // Optimistically add to local state
    setCurrentImages((prev) => {
      const updated = { ...prev };
      if (category === "exterior") {
        // Use isThumbnail from uploadedImage if available, otherwise use parameter
        const finalIsThumbnail =
          (uploadedImage as any).isThumbnail !== undefined
            ? (uploadedImage as any).isThumbnail
            : isThumbnail || false;
        updated.exterior = [
          ...(prev.exterior || []),
          {
            ...uploadedImage,
            isThumbnail: finalIsThumbnail,
          } as VehicleCardExteriorImage,
        ];
        updated.exterior = sortExteriorImages(updated.exterior);
      } else if (category === "interior") {
        updated.interior = [
          ...(prev.interior || []),
          uploadedImage as FileObject,
        ];
      } else if (category === "damage") {
        updated.damage = [
          ...(prev.damage || []),
          { ...uploadedImage, comment: "" },
        ];
      } else if (category === "service") {
        updated.serviceImages = [...(prev.serviceImages || []), uploadedImage];
      } else if (category === "tires" && tireType) {
        updated.tires = {
          ...prev.tires,
          [tireType]: [
            ...(prev.tires?.[tireType] || []),
            uploadedImage as FileObject,
          ],
        };
      }
      return updated;
    });

    // Remove from uploading set after a short delay (to show loading indicator)
    setTimeout(() => {
      setUploadingImageIds((prev) => {
        const next = new Set(prev);
        next.delete(uploadedImage.id);
        return next;
      });
    }, 500);

    return uploadedImage.id;
  };

  // Open file picker for multiple files (max 5)
  const openFilePicker = (
    section: keyof VehicleCardImages,
    isThumbnail: boolean = false,
    tireType?: "primary" | "secondary",
  ) => {
    if (!vehicleCardId) {
      toast.error(vehicleCardUILabels(language).errorTitle, { description: vehicleCardUILabels(language).vehicleCardIdMissing });
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = !isThumbnail; // Only allow single file for thumbnail
    input.onchange = async (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (files.length > 0) {
        // Get current image count for this section
        let currentCount = 0;
        let maxForSection = 10; // Default max

        if (section === "exterior") {
          const regularImages = (currentImages.exterior || []).filter(
            (img: VehicleCardExteriorImage) => !img.isThumbnail,
          );
          currentCount = isThumbnail ? 0 : regularImages.length;
          maxForSection = 15; // 15 regular + 1 thumbnail = 16 total
        } else if (section === "interior") {
          currentCount = (currentImages.interior || []).length;
          maxForSection = 15;
        } else if (section === "damage") {
          currentCount = (currentImages.damage || []).length;
          maxForSection = 10;
        } else if (section === "serviceImages") {
          currentCount = (currentImages.serviceImages || []).length;
          maxForSection = 10;
        } else if (section === "tires" && tireType) {
          currentCount = (currentImages.tires?.[tireType] || []).length;
          maxForSection = 5; // 5 per tire type
        }

        // Validate max 5 files for non-thumbnail uploads
        if (!isThumbnail && files.length > 5) {
          toast.error("Fel", { description: vehicleCardUILabels(language).max5ImagesAtOnce });
          input.value = ""; // Clear input
          return;
        }

        // Validate that current count + new files doesn't exceed max
        const maxNewFiles = isThumbnail
          ? 1
          : Math.min(5, maxForSection - currentCount);
        if (maxNewFiles <= 0) {
          toast.error("Fel", {
            description: vehicleCardUILabels(language).maxImagesReached.replace(
              "{max}",
              String(maxForSection),
            ),
          });
          input.value = ""; // Clear input
          return;
        }

        if (!isThumbnail && files.length > maxNewFiles) {
          toast.error("Fel", {
            description: vehicleCardUILabels(language)
              .maxImagesExceeded.replace("{remaining}", String(maxNewFiles))
              .replace("{max}", String(maxForSection))
              .replace("{current}", String(currentCount)),
          });
          input.value = ""; // Clear input
          return;
        }

        // For thumbnail, only process first file. For regular, limit to maxNewFiles
        const filesToProcess = isThumbnail
          ? files.slice(0, 1)
          : files.slice(0, maxNewFiles);

        // Track loading state for exterior section (thumbnail vs regular)
        if (section === "exterior") {
          if (isThumbnail) {
            setUploadingThumbnail(true);
          } else {
            setUploadingRegularExterior(true);
          }
        }

        // Process all files
        const uploadPromises = filesToProcess.map((file) =>
          processImageFile(file, section, isThumbnail, tireType).catch(
            (error) => {
              toast.error(vehicleCardUILabels(language).imageUploadFailed, {
                description: error instanceof Error
                    ? error.message
                    : vehicleCardUILabels(language).imageUploadFailed,
              });
              return null;
            },
          ),
        );

        // Wait for all uploads to complete
        await Promise.all(uploadPromises);

        // Clear loading state for exterior section
        if (section === "exterior") {
          if (isThumbnail) {
            setUploadingThumbnail(false);
          } else {
            setUploadingRegularExterior(false);
          }
        }

        // Show success toast
        const vc = vehicleCardUILabels(language);
        if (filesToProcess.length === 1) {
          toast.success(vc.imageUploaded, {
            description: vc.imageUploadedDesc.replace(
              "{name}",
              filesToProcess[0].name,
            ),
          });
        } else {
          toast.success(vc.imagesUploaded, {
            description: vc.imagesUploadedCountDesc.replace(
              "{count}",
              String(filesToProcess.length),
            ),
          });
        }
      }
    };
    input.click();
  };

  const removeImage = async (
    section: keyof VehicleCardImages,
    index: number,
    subSection?: "primary" | "secondary",
    imageIdForExterior?: string,
  ) => {
    if (!vehicleCardId) {
      toast.error(vehicleCardUILabels(language).errorTitle, { description: vehicleCardUILabels(language).vehicleCardIdMissing });
      return;
    }

    // Find the image to remove and get its ID
    let imageToRemove:
      | VehicleCardExteriorImage
      | FileObject
      | VehicleCardDamageImage
      | FileObject
      | FileObject
      | null = null;

    // Store the removed image for potential rollback
    const previousState: VehicleCardImages = { ...currentImages };

    if (section === "exterior") {
      const exteriorArr = currentImages.exterior || [];
      imageToRemove = imageIdForExterior
        ? (exteriorArr.find((img) => img.id === imageIdForExterior) ??
          exteriorArr[index])
        : exteriorArr[index];
      const removeBy = imageIdForExterior
        ? (img: { id: string }) => img.id !== imageIdForExterior
        : (_: unknown, i: number) => i !== index;
      setCurrentImages((prev) => ({
        ...prev,
        exterior: prev.exterior?.filter(removeBy) || [],
      }));
    } else if (section === "interior") {
      imageToRemove = currentImages.interior?.[index];
      setCurrentImages((prev) => ({
        ...prev,
        interior: prev.interior?.filter((_, i) => i !== index) || [],
      }));
    } else if (section === "damage") {
      imageToRemove = currentImages.damage?.[index];
      setCurrentImages((prev) => ({
        ...prev,
        damage: prev.damage?.filter((_, i) => i !== index) || [],
      }));
    } else if (section === "serviceImages") {
      imageToRemove = currentImages.serviceImages?.[index];
      setCurrentImages((prev) => ({
        ...prev,
        serviceImages: prev.serviceImages?.filter((_, i) => i !== index) || [],
      }));
    } else if (section === "tires" && subSection) {
      imageToRemove = currentImages.tires?.[subSection]?.[index];
      setCurrentImages((prev) => ({
        ...prev,
        tires: {
          ...prev.tires,
          [subSection]:
            prev.tires?.[subSection]?.filter((_, i) => i !== index) || [],
        },
      }));
    }

    if (!imageToRemove?.id) {
      // Rollback if no image found
      setCurrentImages(previousState);
      toast.error(vehicleCardUILabels(language).errorTitle, { description: vehicleCardUILabels(language).couldNotFindImageId });
      return;
    }

    // Add to deleting set for loading indicator
    setDeletingImageIds((prev) => new Set(prev).add(imageToRemove.id));

    // Call the hook to remove image - cache update in onMutate will handle sync
    try {
      await removeVehicleCardImage({
        vehicleCardId,
        imageId: imageToRemove.id,
      });

      // Remove from deleting set
      setDeletingImageIds((prev) => {
        const next = new Set(prev);
        next.delete(imageToRemove.id);
        return next;
      });

      // Image already removed optimistically, just show success
      toast.success(vehicleCardUILabels(language).imageRemoved, { description: vehicleCardUILabels(language).imageRemovedDesc });
    } catch (error) {
      // Remove from deleting set
      setDeletingImageIds((prev) => {
        const next = new Set(prev);
        next.delete(imageToRemove.id);
        return next;
      });

      // Rollback local state on error
      setCurrentImages(previousState);
      toast.error(vehicleCardUILabels(language).imageRemoveFailed, { description: vehicleCardUILabels(language).imageRemoveFailedDesc });
    }
  };

  const updateDamageComment = useCallback((index: number, comment: string) => {
    // This is only for local state when typing, actual save happens in saveComment
    setCurrentImages((prev) => ({
      ...prev,
      damage:
        prev.damage?.map((damage, i) =>
          i === index ? { ...damage, comment } : damage,
        ) || [],
    }));
  }, []);

  const addServiceDetail = async () => {
    if (!vehicleCardId) {
      toast.error(vehicleCardUILabels(language).errorTitle, { description: vehicleCardUILabels(language).vehicleCardIdMissing });
      return;
    }

    if (!newServiceDetail.serviceType || !newServiceDetail.mileage) {
      toast.error("Fel", { description: vehicleCardUILabels(language).fillServiceTypeAndMileage });
      return;
    }

    const serviceDetail = {
      id: `service_${Date.now()}`,
      date: new Date(newServiceDetail.date),
      mileage: parseInt(newServiceDetail.mileage),
      serviceType: newServiceDetail.serviceType,
      notes: newServiceDetail.notes,
    };

    // Update local state immediately for better UX
    setCurrentImages((prev) => ({
      ...prev,
      serviceDetails: {
        serviceDetails: [
          ...(Array.isArray(prev.serviceDetails?.serviceDetails)
            ? prev.serviceDetails.serviceDetails
            : []),
          serviceDetail,
        ],
      },
    }));

    // Reset form
    setNewServiceDetail({
      date: toInputDate(new Date()),
      mileage: "",
      serviceType: "",
      notes: "",
    });
    setShowServiceDetailDialog(false);

    // Call the hook to save service detail to backend
    try {
      await addVehicleCardServiceDetail({
        vehicleCardId,
        serviceDetail,
      });
      toast.success(vehicleCardUILabels(language).serviceDetailAdded, { description: vehicleCardUILabels(language).serviceDetailAddedDesc });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Något gick fel när servicedetaljen skulle läggas till";
      toast.error(vehicleCardUILabels(language).addFailed, { description: errorMessage });
      // Rollback local state on error
      setCurrentImages((prev) => {
        const currentDetails = prev.serviceDetails?.serviceDetails || [];
        const filtered = currentDetails.filter(
          (detail) => detail.id !== serviceDetail.id,
        );
        return {
          ...prev,
          serviceDetails: {
            serviceDetails: filtered,
          },
        };
      });
    }
  };

  const removeServiceDetail = async (index: number) => {
    if (!vehicleCardId) {
      toast.error(vehicleCardUILabels(language).errorTitle, { description: vehicleCardUILabels(language).vehicleCardIdMissing });
      return;
    }

    const serviceDetailToRemove =
      currentImages.serviceDetails?.serviceDetails?.[index];

    if (!serviceDetailToRemove?.id) {
      toast.error("Fel", { description: vehicleCardUILabels(language).couldNotFindServiceDetailId });
      return;
    }

    // Optimistically remove from local state
    setCurrentImages((prev) => ({
      ...prev,
      serviceDetails: {
        serviceDetails:
          prev.serviceDetails?.serviceDetails?.filter((_, i) => i !== index) ||
          [],
      },
    }));

    // Call the hook to remove service detail from backend
    try {
      await removeVehicleCardServiceDetail({
        vehicleCardId,
        serviceDetailId: serviceDetailToRemove.id,
      });
      toast.success(vehicleCardUILabels(language).serviceDetailRemoved, { description: vehicleCardUILabels(language).serviceDetailRemovedDesc });
    } catch (error) {
      toast.error(vehicleCardUILabels(language).imageRemoveFailed, { description: vehicleCardUILabels(language).serviceDetailRemoveFailedDesc });
      // Rollback local state on error
      setCurrentImages((prev) => ({
        ...prev,
        serviceDetails: {
          serviceDetails: [
            ...(prev.serviceDetails?.serviceDetails || []).slice(0, index),
            serviceDetailToRemove,
            ...(prev.serviceDetails?.serviceDetails || []).slice(index),
          ],
        },
      }));
    }
  };

  const toggleCommentExpansion = (index: number) => {
    setExpandedComments((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
    // Initialize comment text if opening
    if (!expandedComments[index]) {
      const currentComment = currentImages.damage?.[index]?.comment || "";
      setCommentTexts((prev) => ({
        ...prev,
        [index]: currentComment,
      }));
    }
  };

  const saveComment = async (index: number) => {
    if (!vehicleCardId) {
      toast.error(vehicleCardUILabels(language).errorTitle, { description: vehicleCardUILabels(language).vehicleCardIdMissing });
      return;
    }

    const comment = commentTexts[index] || "";
    const imageToUpdate = currentImages.damage?.[index];

    if (!imageToUpdate?.id) {
      toast.error(vehicleCardUILabels(language).errorTitle, { description: vehicleCardUILabels(language).couldNotFindImageId });
      return;
    }

    // Update local state immediately for better UX
    setCurrentImages((prev) => ({
      ...prev,
      damage:
        prev.damage?.map((damage, i) =>
          i === index ? { ...damage, comment } : damage,
        ) || [],
    }));

    setExpandedComments((prev) => ({
      ...prev,
      [index]: false,
    }));

    // Call the hook to save comment to backend
    try {
      await updateVehicleCardDamageImageComment({
        vehicleCardId,
        imageId: imageToUpdate.id,
        comment,
      });
      toast.success(vehicleCardUILabels(language).commentSaved, { description: vehicleCardUILabels(language).commentSavedDesc });
    } catch (error) {
      toast.error(vehicleCardUILabels(language).commentSaveFailed, { description: vehicleCardUILabels(language).commentSaveFailedDesc });
      // Rollback local state on error
      setCurrentImages((prev) => ({
        ...prev,
        damage:
          prev.damage?.map((damage, i) =>
            i === index
              ? { ...damage, comment: imageToUpdate.comment || "" }
              : damage,
          ) || [],
      }));
    }
  };

  const cancelComment = (index: number) => {
    setExpandedComments((prev) => ({
      ...prev,
      [index]: false,
    }));
    setCommentTexts((prev) => ({
      ...prev,
      [index]: "",
    }));
  };

  const ImageUploadCard = ({
    title,
    section,
    images: sectionImages,
    required = false,
    maxImages = 10,
    subSection,
  }: {
    title: string;
    section: keyof VehicleCardImages;
    images: (
      | VehicleCardExteriorImage
      | FileObject
      | VehicleCardDamageImage
      | FileObject
      | FileObject
    )[];
    required?: boolean;
    maxImages?: number;
    subSection?: "primary" | "secondary";
  }) => {
    // Ensure sectionImages is always an array
    const safeSectionImages = Array.isArray(sectionImages) ? sectionImages : [];

    // For exterior: separate thumbnail from regular images
    const isExterior = section === "exterior";
    const thumbnailImage = isExterior
      ? (safeSectionImages as VehicleCardExteriorImage[]).find(
          (img) => img.isThumbnail,
        )
      : null;
    const regularImages = isExterior
      ? (safeSectionImages as VehicleCardExteriorImage[]).filter(
          (img) => !img.isThumbnail,
        )
      : safeSectionImages;

    // Count images correctly (exclude thumbnail from regular count for exterior)
    const imageCount = isExterior
      ? (thumbnailImage ? 1 : 0) + regularImages.length
      : safeSectionImages.length;

    const canAddMore = imageCount < maxImages;
    const canAddThumbnail = isExterior && !thumbnailImage;
    const canAddRegular = isExterior
      ? regularImages.length < maxImages - (thumbnailImage ? 1 : 0)
      : canAddMore;

    // For exterior: show thumbnail separately, then regular images
    if (isExterior) {
      return (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {title}
              {required && (
                <Badge variant="destructive">
                  {vehicleCardUILabels(language).required}
                </Badge>
              )}
              <Badge variant="outline">
                {isExterior
                  ? `${regularImages.length}/${maxImages - 1}` // Don't count thumbnail in regular count
                  : `${imageCount}/${maxImages}`}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Thumbnail section */}
            <div>
              <h4 className="font-semibold mb-3">
                {vehicleCardUILabels(language).thumbnail}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {thumbnailImage && (
                  <div className="relative">
                    <div className="relative group">
                      {uploadingImageIds.has(thumbnailImage.id) && (
                        <div className="absolute inset-0 bg-black/50 rounded flex items-center justify-center z-10">
                          <Loader2 className="h-6 w-6 animate-spin text-white" />
                        </div>
                      )}
                      <img
                        src={thumbnailImage.url}
                        alt="Thumbnail"
                        className="w-full h-24 object-cover rounded border-2 border-blue-500 cursor-pointer"
                        onClick={() =>
                          setSelectedImage({
                            url: thumbnailImage.url,
                            alt: "Thumbnail",
                          })
                        }
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const index = safeSectionImages.findIndex(
                            (img) => img.id === thumbnailImage.id,
                          );
                          if (index !== -1) {
                            removeImage(
                              section,
                              index,
                              subSection,
                              isExterior ? thumbnailImage.id : undefined,
                            );
                          }
                        }}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
                      >
                        <X className="h-5 w-5" />
                      </button>
                      <Badge
                        variant="secondary"
                        className="absolute -top-1 -left-1 bg-blue-500 text-white text-xs"
                      >
                        {vehicleCardUILabels(language).thumbnail}
                      </Badge>
                    </div>
                  </div>
                )}
                {canAddThumbnail && (
                  <button
                    onClick={() => openFilePicker(section, true, subSection)}
                    disabled={uploadingThumbnail}
                    className="w-full h-24 border-2 border-dashed border-blue-500 rounded flex flex-col items-center justify-center hover:border-blue-600 transition-colors disabled:opacity-50"
                  >
                    {uploadingThumbnail ? (
                      <Loader2 className="h-6 w-6 text-primary/70 animate-spin" />
                    ) : (
                      <>
                        <Upload className="h-6 w-6 text-gray-400" />
                        <span className="text-xs text-gray-500 mt-1 text-center whitespace-normal px-1">
                          {vehicleCardUILabels(language).uploadThumbnail}
                        </span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Regular images section */}
            <div>
              <h4 className="font-semibold mb-3">
                {vehicleCardUILabels(language).images}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {regularImages.map((img, index) => {
                  const actualIndex = safeSectionImages.findIndex(
                    (exteriorImg) => exteriorImg.id === img.id,
                  );
                  const isUploading = uploadingImageIds.has(img.id);
                  const isDeleting = deletingImageIds.has(img.id);

                  return (
                    <div key={img.id || index} className="relative">
                      <div className="relative group">
                        {(isUploading || isDeleting) && (
                          <div className="absolute inset-0 bg-black/50 rounded flex items-center justify-center z-10">
                            <Loader2 className="h-6 w-6 animate-spin text-white" />
                          </div>
                        )}
                        <img
                          src={img.url}
                          alt={`${title} ${index + 1}`}
                          className={`w-full h-24 object-cover rounded border cursor-pointer ${
                            isDeleting ? "opacity-50" : ""
                          }`}
                          onClick={() =>
                            !isDeleting &&
                            setSelectedImage({
                              url: img.url,
                              alt: `${title} ${index + 1}`,
                            })
                          }
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(
                              section,
                              actualIndex,
                              subSection,
                              isExterior ? img.id : undefined,
                            );
                          }}
                          disabled={isRemovingVehicleCardImage || isDeleting}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10 disabled:opacity-50"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {canAddRegular && (
                  <button
                    onClick={() => openFilePicker(section, false, subSection)}
                    disabled={uploadingRegularExterior}
                    className="w-full h-24 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center hover:border-gray-400 transition-colors disabled:opacity-50"
                  >
                    {uploadingRegularExterior ? (
                      <Loader2 className="h-6 w-6 text-primary/70 animate-spin" />
                    ) : (
                      <>
                        <Upload className="h-6 w-6 text-gray-400" />
                        <span className="text-xs text-gray-500 mt-1 text-center whitespace-normal px-1">
                          {vehicleCardUILabels(language).uploadImagesMax5}
                        </span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    // For other sections: simple image grid with upload button
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {title}
            {required && (
              <Badge variant="destructive">
                {vehicleCardUILabels(language).required}
              </Badge>
            )}
            <Badge variant="outline">
              {imageCount}/{maxImages}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {regularImages.map((image, index) => {
              const isUploading = uploadingImageIds.has(image.id);
              const isDeleting = deletingImageIds.has(image.id);

              return (
                <div key={image.id || index} className="space-y-2">
                  <div className="relative group">
                    {(isUploading || isDeleting) && (
                      <div className="absolute inset-0 bg-black/50 rounded flex items-center justify-center z-10">
                        <Loader2 className="h-6 w-6 animate-spin text-white" />
                      </div>
                    )}
                    <img
                      src={image.url}
                      alt={`${title} ${index + 1}`}
                      className={`w-full h-24 object-cover rounded border cursor-pointer ${
                        isDeleting ? "opacity-50" : ""
                      }`}
                      onClick={() =>
                        !isDeleting &&
                        setSelectedImage({
                          url: image.url,
                          alt: `${title} ${index + 1}`,
                        })
                      }
                    />
                    <button
                      onClick={() =>
                        removeImage(
                          section,
                          safeSectionImages.indexOf(image),
                          subSection,
                        )
                      }
                      disabled={isRemovingVehicleCardImage || isDeleting}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                    >
                      <X className="w-5 h-5" />
                    </button>
                    {section === "damage" && (
                      <button
                        onClick={() => toggleCommentExpansion(index)}
                        className="absolute -top-2 -left-2 bg-blue-500 text-white rounded-full p-1.5"
                        title={vehicleCardUILabels(language).addComment}
                      >
                        <MessageSquare className="w-5 h-5" />
                      </button>
                    )}
                  </div>

                  {/* Comment section directly under each image */}
                  {section === "damage" && (
                    <div className="space-y-1">
                      {/* Show existing comment */}
                      {"comment" in image &&
                        image.comment &&
                        !expandedComments[index] && (
                          <div className="text-sm text-primary/60 p-2 rounded break-words overflow-wrap-anywhere">
                            {image.comment}
                          </div>
                        )}

                      {/* Show textarea when expanded */}
                      {expandedComments[index] && (
                        <div className="space-y-2">
                          <Textarea
                            value={commentTexts[index] || ""}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              setCommentTexts((prev) => ({
                                ...prev,
                                [index]: newValue,
                              }));
                            }}
                            onFocus={(e) => {
                              // Ensure cursor stays at end when focused to prevent jumping
                              const length = e.target.value.length;
                              e.target.setSelectionRange(length, length);
                            }}
                            dir="ltr"
                            style={{
                              direction: "ltr",
                              textAlign: "left",
                              unicodeBidi: "bidi-override",
                              fontSize: "16px",
                            }}
                            placeholder={
                              vehicleCardUILabels(language).describeDamage
                            }
                            className="w-full text-left"
                            rows={3}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => saveComment(index)}
                              disabled={isUpdatingVehicleCardDamageImageComment}
                              className="text-xs"
                            >
                              {isUpdatingVehicleCardDamageImageComment
                                ? vehicleCardUILabels(language).saving
                                : vehicleCardUILabels(language).save}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => cancelComment(index)}
                              className="text-xs"
                            >
                              {vehicleCardUILabels(language).cancel}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Add more button */}
            {canAddMore && (
              <button
                onClick={() => openFilePicker(section, false, subSection)}
                disabled={isAddingVehicleCardImage}
                className="w-full h-24 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center hover:border-gray-400 transition-colors disabled:opacity-50"
              >
                {isAddingVehicleCardImage ? (
                  <Loader2 className="h-6 w-6 text-primary/70 animate-spin" />
                ) : (
                  <>
                    <Upload className="h-6 w-6 text-gray-400" />
                    <span className="text-xs text-gray-500 mt-1 text-center whitespace-normal px-1">
                      {vehicleCardUILabels(language).uploadImagesMax5}
                    </span>
                  </>
                )}
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Show loading spinner when component first loads or when tab is opened
  if (isLoading && vehicleCardId) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">
          {vehicleCardUILabels(language).loadingImages}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <h2 className="text-2xl font-bold">
            {vehicleCardUILabels(language).vehicleImages}
          </h2>
          <Dialog>
            <DialogTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 px-2 py-1 rounded-lg border-2 border-primary/30 hover:bg-accent/50 text-blue-400 text-xs font-semibold focus:outline-none"
                aria-label="Info om bildkrav"
              >
                {vehicleCardUILabels(language).info}{" "}
                <Info className="w-4 h-4" />
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-xs">
              <ModalInfoVehicleImages />
            </DialogContent>
          </Dialog>
        </div>
        <p className="text-primary/60">
          {vehicleCardUILabels(language).manageImagesForVehicle}
        </p>
      </div>

      <ImageUploadCard
        title={vehicleCardUILabels(language).exterior}
        section="exterior"
        images={(currentImages.exterior || []).filter(
          (img) => img.frameIndex == null,
        )}
        maxImages={16}
      />

      <ImageUploadCard
        title={vehicleCardUILabels(language).interior}
        section="interior"
        images={currentImages.interior || []}
        maxImages={15}
      />

      <ImageUploadCard
        title={vehicleCardUILabels(language).damage}
        section="damage"
        images={currentImages.damage || []}
        maxImages={10}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {vehicleCardUILabels(language).service}
            <Badge variant="outline">
              {currentImages.serviceImages?.length || 0}/10
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">
              {vehicleCardUILabels(language).serviceImages}
            </h4>
            <ImageUploadCard
              title=""
              section="serviceImages"
              images={currentImages.serviceImages || []}
              maxImages={10}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium">
                {vehicleCardUILabels(language).serviceDetails}
              </h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowServiceDetailDialog(true)}
                className="flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                {vehicleCardUILabels(language).addServiceDetails}
              </Button>
            </div>

            {currentImages.serviceDetails?.serviceDetails?.length > 0 ? (
              <div className="space-y-2">
                {currentImages.serviceDetails?.serviceDetails?.map(
                  (detail, index) => (
                    <div
                      key={detail.id || index}
                      className="p-3 border rounded-lg"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium">
                            {detail.serviceType}
                          </div>
                          <div className="text-sm text-primary/60">
                            {detail.date instanceof Date
                              ? detail.date.toLocaleDateString("sv-SE")
                              : detail.date}
                            {" • "}
                            {formatMileage(detail.mileage)} mil
                          </div>
                          {detail.notes && (
                            <div className="text-sm mt-1">{detail.notes}</div>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeServiceDetail(index)}
                          disabled={isRemovingVehicleCardServiceDetail}
                          className="text-red-500 hover:text-red-700 disabled:opacity-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ),
                )}
              </div>
            ) : (
              <div className="text-center py-4 text-primary/50">
                {vehicleCardUILabels(language).noServiceDetailsYet}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {vehicleCardUILabels(language).tires}
            <Badge variant="outline">
              {(currentImages.tires?.primary?.length || 0) +
                (currentImages.tires?.secondary?.length || 0)}
              /20
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">
              {vehicleCardUILabels(language).primaryTires}
            </h4>
            <ImageUploadCard
              title=""
              section="tires"
              images={currentImages.tires?.primary || []}
              maxImages={5}
              subSection="primary"
            />
          </div>

          <div>
            <h4 className="font-medium mb-2">
              {vehicleCardUILabels(language).secondaryTires}
            </h4>
            <ImageUploadCard
              title=""
              section="tires"
              images={currentImages.tires?.secondary || []}
              maxImages={5}
              subSection="secondary"
            />
          </div>
        </CardContent>
      </Card>

      {/* Image preview dialog */}
      <Dialog
        open={!!selectedImage}
        onOpenChange={() => setSelectedImage(null)}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{selectedImage?.alt}</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="flex justify-center">
              <img
                src={selectedImage.url}
                alt={selectedImage.alt}
                className="max-w-full max-h-[80vh] object-contain rounded"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Service Detail Dialog */}
      <Dialog
        open={showServiceDetailDialog}
        onOpenChange={setShowServiceDetailDialog}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {vehicleCardUILabels(language).addServiceDetail}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {vehicleCardUILabels(language).date}
              </label>
              <input
                type="date"
                value={newServiceDetail.date}
                onChange={(e) =>
                  setNewServiceDetail((prev) => ({
                    ...prev,
                    date: e.target.value,
                  }))
                }
                className="w-full p-2 border rounded bg-card"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {vehicleCardUILabels(language).mileageAtService}
              </label>
              <input
                type="number"
                value={newServiceDetail.mileage}
                onChange={(e) =>
                  setNewServiceDetail((prev) => ({
                    ...prev,
                    mileage: e.target.value,
                  }))
                }
                className="w-full p-2 border rounded bg-card"
                placeholder={vehicleCardUILabels(language).enterMileage}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {vehicleCardUILabels(language).serviceType}
              </label>
              <input
                type="text"
                value={newServiceDetail.serviceType}
                onChange={(e) =>
                  setNewServiceDetail((prev) => ({
                    ...prev,
                    serviceType: e.target.value,
                  }))
                }
                className="w-full p-2 border rounded bg-card"
                placeholder={
                  vehicleCardUILabels(language).serviceTypePlaceholder
                }
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {vehicleCardUILabels(language).notes}
              </label>
              <Textarea
                value={newServiceDetail.notes}
                onChange={(e) =>
                  setNewServiceDetail((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
                className="w-full h-20"
                placeholder={vehicleCardUILabels(language).notesPlaceholder}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowServiceDetailDialog(false)}
              >
                {vehicleCardUILabels(language).cancel}
              </Button>
              <Button
                type="button"
                onClick={addServiceDetail}
                disabled={
                  !newServiceDetail.serviceType ||
                  !newServiceDetail.mileage ||
                  isAddingVehicleCardServiceDetail
                }
              >
                {vehicleCardUILabels(language).add}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const VehicleCardImages = VehicleCardImagesComponent;
export default VehicleCardImages;
